import { Injectable, Injector, inject } from '@angular/core';
import {
  Firestore,
  collectionData,
  doc,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { COLLECTION, Notification, NotificationDocument } from '@parkingless/interface';
import { chunkForIn } from './query.util';
import { inFirebaseContext } from './injection.util';
import { typedCollection } from './typed-collection.util';

/**
 * Per-garage notification documents.
 *
 * One document per garage, shared by everyone with a role in it — a service request
 * approved by one employee is approved for all of them. The signed-in user's own
 * notifications live on their user document instead and are written through UserService.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly db = inject(Firestore);
  private readonly injector = inject(Injector);
  private readonly notifications = typedCollection<NotificationDocument>(this.db, COLLECTION.notification);

  /** The notification documents of the garages the user has a role in. */
  watchForGarages(garageIds: readonly string[]): Observable<NotificationDocument[]> {
    if (garageIds.length === 0) {
      return of([]);
    }

    // Chunked because Firestore's `in` operator takes at most 30 values.
    const streams = chunkForIn([...garageIds]).map((chunk) =>
      inFirebaseContext(
        this.injector,
        () =>
          collectionData(query(this.notifications, where('garageId', 'in', chunk))),
      ),
    );

    return combineLatest(streams).pipe(map((groups) => groups.flat()));
  }

  /** Replaces a garage's notification list. */
  updateList(garageId: string, notificationList: Notification[]): Promise<void> {
    return updateDoc(doc(this.notifications, garageId), { notificationList });
  }
}
